import React, { useCallback, useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { fetchReviewData } from '../reviews/reviews_list_common';
import { ReviewPublicationStatusType } from '../../interfaces/review';
import ReviewSnippetSelectionPane from './review_snippet_selection_pane';
import { TReviewInfo, TReviewShowcaseResponseItem, TReviewSnippetsPicker, TSnippetItem } from './types';
import PublishedReviewsRow from './published_reviews_row';
import { secureHttp } from '../../utils/request';
// import ReviewSnippetsPreviewModal from './review_snippets_preview_modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import { getSingleProperty } from '../../utils/common';

const ReviewSnippetsPicker = ({ selNHID }: TReviewSnippetsPicker) => { 

    const { Properties, Session, Org } = useSelector(
        ({ SessionState }: RootState) => SessionState
    );
    
    const { SessionType } = Session;
    const singleProperty = getSingleProperty(SessionType, selNHID, Properties);

    const [cards, setCards] = useState<TSnippetItem[]>([]);

    const showcaseQueryKey = ['review', 'showcase', selNHID];

    const results = useQueries({
        queries: [
            { 
                queryKey: ['reviews', 'published', selNHID], 
                queryFn: async () => {
                            
                    const params = { NHID: selNHID, PublicationStatusKeyName: ReviewPublicationStatusType.Published };
        
                    const [tableData]: [TReviewInfo[], number, number] = await fetchReviewData(params);
        
                    return {
                        data: tableData
                    };
                },
                enabled: !!selNHID 
            },
            {         
                queryKey: showcaseQueryKey, 
                queryFn: async () => {
                    const params = { NHID: selNHID };
        
                    const { data } = await secureHttp.get(`/reviewshowcase/${selNHID}`, { params });
        
                    return data;
                },
                enabled: !!selNHID, 
            },
        ],
    });

    const reviewsArr = results.length ? results[0].data?.data || [] : [];
    const showcaseArr: TReviewShowcaseResponseItem[] = results.length ? results[1].data?.data || [] : [];

    useEffect(() => {
        const newDefaultState = showcaseArr.map((item) => {
            return {
                snippetID: item.Sequence,
                snippetText: item.Snippet,
                reviewContext: {
                    sourceReviewID: item.ReviewID,
                    sourceReviewConnectionName: item.ReviewerConnectionName ?? '',
                    sourceReviewerDisplayName: item.ReviewerDisplayName ?? '',
                },
            };
        });
        if (cards.length === 0 && newDefaultState.length === 0) {
            return;
        }
        setCards(newDefaultState);
    }, [showcaseArr]);

    const addCard = useCallback((cardItem: TSnippetItem) => {
        setCards((prevCards: TSnippetItem[]) => {
            
            const newCardArr = [ ...prevCards ];
            const snippetID = prevCards.length ? Math.max(...prevCards.map(item => item.snippetID)) : 0;

            const newCardItem = { 
                ...cardItem,
                snippetID: snippetID + 1
            };

            const duplicateReviewFound = prevCards.some(prevItem => prevItem?.reviewContext?.sourceReviewID === cardItem?.reviewContext?.sourceReviewID);
        
            if (duplicateReviewFound) {
                newCardItem.errorContext = {
                    hasError: true,
                    errorMsg: 'Duplicate Source ReviewID'
                };
            }

            if (newCardArr.length >= 5) {
                newCardItem.errorContext = {
                    hasError: true,
                    errorMsg: 'Maximum snippet entries exceeded'
                };
            }

            newCardArr.push(newCardItem);

            return newCardArr;
            
        });
    }, [cards]);

    return (
        <div className="mt-4 sm:mt-6 md:mt-8 2xl:mt-10">
            <h3 className="font-semibold leading-snug text-lg xl:text-center">
                Select up to 5 different snippets from published reviews to showcase on the facility's home page.
            </h3>
            <ul className="mt-3 text-xs flex flex-col xl:flex-row xl:justify-center xl:gap-6 xl:mt-4">
                <li className="flex items-center mb-2 xl:m-0 xl:max-w-72">
                    <span className="px-3 py-2 rounded-xl bg-brand_primary text-white flex items-center justify-center text-lg font-bold me-2 whitespace-nowrap">
                        Step 1
                    </span>
                    Select which review you want to showcase.
                </li>
                <li className="flex items-center mb-2 xl:m-0 xl:max-w-72">
                    <span className="px-3 py-2 rounded-xl bg-brand_primary text-white flex items-center justify-center text-lg font-bold me-2 whitespace-nowrap">
                        Step 2
                    </span>
                    <span>
                        Highlight you favorite part of the review <span className="text-xs inline-block">(max 100 chars).</span>
                    </span>
                </li>
                <li className="flex items-center mb-2 xl:m-0 xl:max-w-72">
                    <span className="px-3 py-2 rounded-xl bg-brand_primary text-white flex items-center justify-center text-lg font-bold me-2 whitespace-nowrap">
                        Step 3
                    </span>
                    Click the 'Showcase this review' button.
                </li>
                <li className="flex items-center xl:max-w-72">
                    <span className="px-3 py-2 rounded-xl bg-brand_primary text-white flex items-center justify-center text-lg font-bold me-2 whitespace-nowrap">
                        Step 4
                    </span>
                    Repeat Step 1-3 for up to 5 reviews, then click 'Save'.
                </li>
            </ul>
            <p>
                
            </p>
            <div className="mt-4 sm:mt-6 md:mt-8 2xl:mt-10 grid lg:grid-cols-2 gap-8">
                <div className="ring-1 ring-brand_grey overflow-hidden rounded-md lg:rounded-lg flex flex-col h-full">
                    <div className="bg-brand_faint-blue px-3 sm:px-4 2xl:px-5 py-1 sm:py-2 2xl:py-3 min-h-14 flex items-center">
                        <h4 className="font-semibold leading-none text-lg lg:text-xl">
                            All Published Reviews
                        </h4>
                    </div>
                    <div className="p-3">
                        <div className="flex flex-col gap-3 max-h-[44rem] min-h-[44rem] overflow-auto scrollbar">
                            {reviewsArr.map(review => <PublishedReviewsRow key={review.ReviewID} cards={cards} addCard={addCard} reviewInfo={review} />)}
                        </div>
                    </div>
                </div>
                <div className="ring-1 ring-brand_grey overflow-hidden rounded-md lg:rounded-lg flex flex-col h-full">
                    <div className="bg-brand_faint-blue px-3 sm:px-4 2xl:px-5 py-1 sm:py-2 2xl:py-3 min-h-14 flex items-center">
                        <h4 className="font-semibold leading-none text-lg lg:text-xl">
                            Review Showcase Snippets
                        </h4>
                        <a
                            className="link text-xs bg-white px-2 py-1 inline-block leading-none rounded-lg relative -top-px ms-auto"
                            href={`${singleProperty.WebsiteURL}/${singleProperty.StateSlug}/${singleProperty.CitySlug}/${singleProperty.Slug}`}
                            target="_blank"
                        >
                            view on website
                        </a>
                    </div>
                    <div className="p-3">
                        <div className="flex flex-col gap-3 max-h-[44rem] min-h-[44rem] overflow-auto scrollbar">
                            <ReviewSnippetSelectionPane key={selNHID} cards={cards} reviewShowcaseData={showcaseArr} selNHID={selNHID} setCards={setCards} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewSnippetsPicker;
